import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';

import routes from 'js/core_pre_webpack/routes';
import { SerializedJournal } from 'js/letters/types';
import { get } from 'js/utils/api_utils';

import {
  Navbar,
  LinkItem,
  MenuItem,
  MenuLinkSection,
  MenuLink,
  MenuMessageSection,
} from 'js/design_system/nav/Navbar';
import { ButtonLink } from 'js/design_system/link/ButtonLink';
import { SearchIcon, SearchLabel } from './search/SearchNavbarComponents';
import NavbarSearchContent from './search/NavbarSearchContent';

const Logo = styled.a`
  @import 'stylesheets/design_system/font';
  @include serif__small;

  /* More overriding of libraries.css */
  &,
  &:hover,
  &:active,
  &:focus {
    text-decoration: none;
    color: var(--text-primary, #070a15);
  }
`;

const SmallLogo = ({ journal_slug }: { journal_slug: string }) => (
  <Logo href={routes.journals_url({ journal_slug, source: 'journal-top-nav' })}>
    A
  </Logo>
);

const LargeLogo = ({
  name,
  journal_slug,
}: {
  name: string;
  journal_slug: string;
}) => (
  <Logo href={routes.journals_url({ journal_slug, source: 'journal-top-nav' })}>
    {name}
  </Logo>
);

type JournalNavbarProps = {
  messages_count: number | null;
  journal: SerializedJournal;
  canSeeAboutMenu: boolean;
  initialQuery?: string;
  inDevStyle?: boolean;
};

/* Navbar used on individual journal pages (e.g. Academia Biology) */
const JournalNavbar: React.FC<JournalNavbarProps> = ({
  messages_count,
  journal,
  canSeeAboutMenu,
  initialQuery = '',
  inDevStyle,
}) => {
  const [unreadMentionsCount, setUnreadMentionsCount] = useState(0);

  useEffect(() => {
    const url = routes.unread_count_api_v0_mentions_path({
      subdomain_param: 'api',
    });
    get(url).then(({ count }) => setUnreadMentionsCount(count));
  }, []);

  const journalOptions = [
    { id: 'all', name: 'All journals' },
    { id: journal.slug, name: journal.name },
  ];

  const initialJournal = { id: journal.slug, name: journal.name };

  return (
    <Navbar
      logoSmall={<SmallLogo journal_slug={journal.slug} />}
      logoLarge={<LargeLogo name={journal.name} journal_slug={journal.slug} />}
      messagesCount={messages_count}
      unreadMentionsCount={unreadMentionsCount}
      inDevStyle={inDevStyle}
      items={
        <>
          <MenuItem
            title="Search"
            reference={
              <SearchLabel>
                <SearchIcon />
                <span>Search</span>
              </SearchLabel>
            }
          >
            <NavbarSearchContent
              initialJournal={initialJournal}
              journalOptions={journalOptions}
              initialQuery={initialQuery}
            />
          </MenuItem>

          {journal.show_articles && (
            <LinkItem
              href={routes.journals_articles_index_path({
                journal_slug: journal.slug,
                source: 'journal-top-nav',
              })}
            >
              Articles
            </LinkItem>
          )}
          {journal.show_sections &&
            (journal.subfield_headings ? (
              <MenuItem title="Sections">
                {Object.keys(journal.subfield_headings).map((heading) => (
                  <MenuLinkSection key={heading} title={heading}>
                    {journal.subfield_headings?.[heading].map((section) => (
                      <MenuLink
                        key={section}
                        href={routes.journals_subfields_path({
                          journal_slug: journal.slug,
                          section_name: section,
                          source: 'journal-top-nav',
                        })}
                      >
                        {section}
                      </MenuLink>
                    ))}
                  </MenuLinkSection>
                ))}
              </MenuItem>
            ) : (
              <MenuItem title="Sections">
                <MenuLinkSection title="Sections">
                  {journal.subfields.map((section) => (
                    <MenuLink
                      key={section}
                      href={routes.journals_subfields_path({
                        journal_slug: journal.slug,
                        section_name: section,
                        source: 'journal-top-nav',
                      })}
                    >
                      {section}
                    </MenuLink>
                  ))}
                </MenuLinkSection>
              </MenuItem>
            ))}

          {journal.show_special_issues && (
            <LinkItem
              href={routes.journals_special_issues_index_path({
                journal_slug: journal.slug,
                source: 'journal-top-nav',
              })}
            >
              Special Issues
            </LinkItem>
          )}
          {journal.show_editorial_board && (
            <LinkItem
              href={routes.editorial_board_journals_about_path({
                journal_slug: journal.slug,
                source: 'journal-top-nav',
              })}
            >
              Editorial Board
            </LinkItem>
          )}

          {canSeeAboutMenu && (
            <MenuItem title="About the journal">
              <MenuLinkSection title="General information">
                <MenuLink
                  href={routes.aims_and_scope_journals_about_path({
                    journal_slug: journal.slug,
                    source: 'journal-top-nav',
                  })}
                >
                  Aims & scope
                </MenuLink>
                <MenuLink
                  href={routes.indexing_and_archiving_journals_about_path({
                    journal_slug: journal.slug,
                    source: 'journal-top-nav',
                  })}
                >
                  Indexing & archiving
                </MenuLink>
                <MenuLink
                  href={routes.editorial_process_journals_about_path({
                    journal_slug: journal.slug,
                  })}
                >
                  Editorial process
                </MenuLink>
                <MenuLink
                  href={routes.editorial_policies_journals_about_path({
                    journal_slug: journal.slug,
                    source: 'journal-top-nav',
                  })}
                >
                  Editorial policies
                </MenuLink>
                <MenuLink
                  href={routes.open_access_policy_journals_about_path({
                    journal_slug: journal.slug,
                    source: 'journal-top-nav',
                  })}
                >
                  Open access policy
                </MenuLink>
                <MenuLink
                  href={routes.article_processing_charges_journals_about_path({
                    journal_slug: journal.slug,
                    source: 'journal-top-nav',
                  })}
                >
                  Article processing charges
                </MenuLink>
                <MenuLink
                  href={routes.faq_journals_about_path({
                    journal_slug: journal.slug,
                    source: 'journal-top-nav',
                  })}
                >
                  FAQ
                </MenuLink>
                <MenuLink
                  href={routes.contact_editorial_office_journals_about_path({
                    journal_slug: journal.slug,
                    source: 'journal-top-nav',
                  })}
                >
                  Contact editorial office
                </MenuLink>
              </MenuLinkSection>
              <MenuLinkSection title="Guidelines">
                <MenuLink
                  href={routes.author_guidelines_journals_about_path({
                    journal_slug: journal.slug,
                    source: 'journal-top-nav',
                  })}
                >
                  Author guidelines
                </MenuLink>
                <MenuLink
                  href={routes.style_guide_journals_about_path({
                    journal_slug: journal.slug,
                    source: 'journal-top-nav',
                  })}
                >
                  Style guide
                </MenuLink>
                <MenuLink
                  href={routes.reviewer_guidelines_journals_about_path({
                    journal_slug: journal.slug,
                    source: 'journal-top-nav',
                  })}
                >
                  Reviewer guidelines
                </MenuLink>
                <MenuLink
                  href={routes.editor_guidelines_journals_about_path({
                    journal_slug: journal.slug,
                    source: 'journal-top-nav',
                  })}
                >
                  Editor guidelines
                </MenuLink>
                <MenuLink
                  href={routes.special_issue_guidelines_journals_about_path({
                    journal_slug: journal.slug,
                    source: 'journal-top-nav',
                  })}
                >
                  Special issue guidelines
                </MenuLink>
              </MenuLinkSection>
              <MenuMessageSection
                title="Other journals by Academia.edu"
                text={`${journal.name} is part of a series of open-access journals powered by Academia.edu`}
                ctaText="View all Academia.edu Journals"
                ctaHref={routes.academia_journals_path({
                  source: 'journal-top-nav',
                })}
              />
            </MenuItem>
          )}
        </>
      }
      ctas={
        journal.can_submit && (
          <ButtonLink
            href={routes.journals_create_new_submission_path({
              journal_id: journal.id,
              journal_slug: journal.slug,
              source: 'journal-top-nav',
            })}
            secondary
            size="sm"
            iconRight="arrow_right_alt"
          >
            Submit an article
          </ButtonLink>
        )
      }
    />
  );
};

export default JournalNavbar;
